import { useState } from 'react';

import { BasketBar } from '@/shared/quotes/AddToQuote';
import type { TariffItem } from '@/shared/tariffs/types';
import { useFamilies, useSuppliers, useTariffSearch } from '@/shared/tariffs/useTariffs';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { SearchIcon } from '@/shared/ui/icons';
import { Spinner } from '@/shared/ui/Spinner';
import { useDebounced } from '@/shared/utils/useDebounced';

import { ArticleDialog, ArticlesTable } from './Articles';

/** Shorter than this, a word finds half the tariffs: the box waits for a second letter. */
const MIN_CHARS = 2;

/**
 * The one box of «Documentos» (contract v10.48): an EAN, a reference written any way
 * (with dots, spaces, the zeros Excel ate), the beginning of one, or words of the
 * description. The back decides which; this only says how it found each one. Only the
 * tariffs in force, with the supplier and the family as filters.
 *
 * For a profile that makes quotes from the tariffs (contract v10.52), each article has its
 * quantity and «Añadir», and the bar above says which draft they go to.
 */
export function TariffSearch() {
  const [typed, setTyped] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [family, setFamily] = useState('');
  const [open, setOpen] = useState<TariffItem | null>(null);
  const q = useDebounced(typed, 300).trim();

  const suppliers = useSuppliers();
  const families = useFamilies(supplierId || undefined);
  const asking = q.length >= MIN_CHARS || supplierId !== '' || family !== '';
  const results = useTariffSearch(
    {
      ...(q.length >= MIN_CHARS ? { q } : {}),
      ...(supplierId ? { supplier_id: supplierId } : {}),
      ...(family ? { family } : {}),
    },
    asking,
  );
  const items = results.data?.items ?? [];
  const withTariffs = (suppliers.data?.items ?? []).filter((each) =>
    (each.price_lists ?? []).some((list) => list.current),
  );

  return (
    <section aria-label="Buscar en las tarifas" className="flex flex-col gap-3">
      <BasketBar />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative w-full max-w-xl">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar en las tarifas"
            placeholder="Referencia, EAN o descripción"
            className="field w-full pl-9"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
        {withTariffs.length > 1 ? (
          <select
            aria-label="Proveedor"
            className="field w-full sm:w-auto"
            value={supplierId}
            onChange={(event) => {
              setSupplierId(event.target.value);
              setFamily('');
            }}
          >
            <option value="">Todos los proveedores</option>
            {withTariffs.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
          </select>
        ) : null}
        {(families.data ?? []).length > 0 ? (
          <select
            aria-label="Familia"
            className="field w-full sm:w-auto sm:max-w-56"
            value={family}
            onChange={(event) => setFamily(event.target.value)}
          >
            <option value="">Todas las familias</option>
            {(families.data ?? []).map((each) => (
              <option key={each} value={each}>
                {each}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {asking && results.isPending ? <Spinner variant="block" label="Buscando" /> : null}
      {results.isError ? (
        <ErrorMessage error={results.error}>No se pudo buscar en las tarifas</ErrorMessage>
      ) : null}
      {asking && results.data && items.length === 0 ? (
        <EmptyState
          icon={<SearchIcon size={24} />}
          title="Nada en las tarifas vigentes"
          hint={
            q
              ? `Ningún artículo con «${q}». Prueba con menos palabras, sin la marca, o con el principio de la referencia.`
              : 'Este proveedor o esta familia no tienen artículos en una tarifa vigente.'
          }
        />
      ) : null}
      {asking && items.length > 0 ? (
        <>
          <ArticlesTable addable items={items} label="Artículos encontrados" onOpen={setOpen} />
          {results.data?.limited ? (
            <p className="text-ink-3 text-[13px]">
              Salen los {items.length} primeros: afina la búsqueda para ver el resto.
            </p>
          ) : null}
        </>
      ) : null}
      {open ? <ArticleDialog addable item={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}
